// ============================================================================
// TeamMemberCard — carte de membre compacte, partagee par l'onglet « Mon equipe »
// de l'accueil et la page /mon-equipe (lot design-mon-equipe-en-une-page)
// ============================================================================
// Une seule carte pour les deux ecrans : pastille d'initiales (GsAvatar), nom,
// ligne de role, etat du sondage en point de couleur, charge et energie quand le
// sondage est recu. Toute la carte est le lien vers la fiche (meme destination
// qu'avant : /profile/{email}?tab=gestion&from=mon-equipe&fromTeam=...).
// M-153 : aucun salaire. M-154 : scores seulement, jamais le commentaire du pulse
// (le DTO serveur ne le porte meme pas).

import { Link } from 'react-router-dom';
import { GsAvatar, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { composePersonRoleSubtitle } from '../../lib/org/derivePersonRole';
import { deriveOrgLevel, orgLevelI18nKey } from '../../lib/org/deriveOrgLevel';
import type { RosterMember } from '../../hooks/useMonEquipe';
import { memberProfileHref, orderLeadersFirst } from './teamMemberOrder';

export function TeamMemberCard({ member, teamId }: { member: RosterMember; teamId: string | null }) {
  const { t } = useTranslation();
  const level = deriveOrgLevel({
    orgLevel: member.orgLevel,
    isTeamDirector: member.isDirector,
  });
  const roleLine = composePersonRoleSubtitle(
    level ? t(orgLevelI18nKey(level)) : null,
    member.jobTitle,
  );
  const displayName = member.fullName ?? member.email;
  const hasScores = member.submitted && member.workloadScore != null && member.energyScore != null;

  return (
    <Link
      to={memberProfileHref(member.email, teamId)}
      data-survey={member.submitted ? 'received' : 'awaiting'}
      className={`${GS_COMPONENTS.card} flex min-h-11 touch-manipulation items-start gap-2.5 p-2.5 transition-shadow hover:shadow-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 sm:p-3`}
    >
      <GsAvatar name={member.fullName ?? member.email.split('@')[0]} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-field font-semibold text-slate-900 dark:text-slate-100">
          {displayName}
        </p>
        {roleLine ? (
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">{roleLine}</p>
        ) : null}
        <div className="mt-1 flex items-start gap-1.5 text-meta text-slate-600 dark:text-slate-300">
          <span
            aria-hidden="true"
            className={`mt-1 h-2 w-2 shrink-0 rounded-full ${member.submitted ? 'bg-green-500' : 'bg-amber-500'}`}
          />
          {hasScores ? (
            <>
              <span className="sr-only">{t('gest:monEquipe.memberCard.surveyReceived')}</span>
              <span className="flex min-w-0 flex-wrap gap-x-2 tabular-nums">
                <span className="whitespace-nowrap">
                  {t('gest:monEquipe.memberCard.workload', { value: member.workloadScore })}
                </span>
                <span className="whitespace-nowrap">
                  {t('gest:monEquipe.memberCard.energy', { value: member.energyScore })}
                </span>
              </span>
            </>
          ) : (
            <span className="min-w-0">
              {member.submitted
                ? t('gest:monEquipe.memberCard.surveyReceived')
                : t('gest:monEquipe.memberCard.surveyAwaiting')}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/** Grille dense : 2 colonnes sur telephone, jusqu'a 6 sur grand ecran. */
export function TeamMemberGrid({ roster, teamId }: { roster: readonly RosterMember[]; teamId: string | null }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
      {orderLeadersFirst(roster).map((member) => (
        <TeamMemberCard key={member.email} member={member} teamId={teamId} />
      ))}
    </div>
  );
}
